import { useMemo, useState, type MouseEvent } from 'react'
import { Activity, BookOpen, Folder, FolderOpen, Radio, Wand2 } from 'lucide-react'
import { toast } from 'sonner'
import { EntityContextMenu } from '@/components/features/entity-context-menu'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { wsNavRowPadLeftPx } from '@/components/layout/left-sidebar.components'
import { useFlowActions, useFlows } from '@/hooks/use-flows'
import { flowPosture, triggerFlowRun, type Flow, type FlowPosture } from '@/lib/api'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import { testIds } from '@/lib/test-ids'
import {
  flowStudioSurfaceFromTabId,
  openFlowStudioSurface,
  rememberFlowTab,
  type FlowStudioSurface,
} from './flow-studio-events'

const NAV: { id: FlowStudioSurface; label: string; icon: typeof Activity }[] = [
  { id: 'pulse', label: 'Pulse', icon: Activity },
  { id: 'gallery', label: 'Gallery', icon: BookOpen },
  { id: 'sources', label: 'Sources', icon: Radio },
  { id: 'composer', label: 'Composer', icon: Wand2 },
]

function postureLabel(flow: { enabled: boolean; shadow?: boolean; paused?: boolean }) {
  if (flow.paused) return 'Paused'
  const posture = flowPosture(flow)
  if (posture === 'disabled') return 'Draft'
  if (posture === 'shadow') return 'Shadow'
  return 'Live'
}

function folderOf(filePath: string) {
  const parts = filePath.split('/')
  parts.pop()
  return parts.join('/') || 'flows'
}

export function FlowStudioSidebar({
  onOpenDocument,
  activeDocumentId,
  dense = false,
}: {
  onOpenDocument: (documentId: string, event?: MouseEvent) => void
  activeDocumentId?: string | null
  dense?: boolean
}) {
  const { flows, loading, error, refetch } = useFlows()
  const { setPosture } = useFlowActions()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<{ x: number; y: number; flow: Flow } | null>(null)
  const activeSurface = flowStudioSurfaceFromTabId(activeDocumentId)

  const grouped = useMemo(() => {
    const groups = new Map<string, Flow[]>()
    for (const flow of flows) {
      const folder = folderOf(flow.file_path || 'flows')
      const list = groups.get(folder) ?? []
      list.push(flow)
      groups.set(folder, list)
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [flows])

  const openFlow = (flowId: string, tab: 'definition' | 'runs', event?: MouseEvent) => {
    rememberFlowTab(flowId, tab)
    onOpenDocument(flowId, event)
  }

  const promote = async (flow: Flow, mode: FlowPosture) => {
    try {
      await setPosture(flow.id, mode)
      await refetch()
    } catch {
      toast.error('Could not change posture')
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-testid={testIds.flowStudioIndex.nav}>
      <nav className="shrink-0 space-y-0.5 border-b border-sidebar-border px-1 py-1">
        {NAV.map((item) => {
          const Icon = item.icon
          return (
            <ModePanelNavItem
              key={item.id}
              dense={dense}
              active={activeSurface === item.id}
              icon={<Icon className="h-4 w-4 shrink-0" />}
              label={item.label}
              onClick={() => openFlowStudioSurface(item.id)}
            />
          )
        })}
      </nav>
      <div className="min-h-0 flex-1 overflow-y-auto py-1" data-testid={testIds.flowStudioIndex.tree}>
        {loading && <p className="px-3 py-2 text-sm text-muted-foreground">Loading flows...</p>}
        {error && <p className="px-3 py-2 text-sm text-red-500">{error.message}</p>}
        {!loading && grouped.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">No flows yet.</p>
        )}
        {grouped.map(([folder, list]) => {
          const expanded = !collapsed.has(folder)
          const FolderIcon = expanded ? FolderOpen : Folder
          return (
            <div key={folder}>
              <SidebarTreeRow
                padLeft={wsNavRowPadLeftPx(0)}
                icon={<FolderIcon className="h-3.5 w-3.5" />}
                label={folder}
                chevron={{
                  expanded,
                  onClick: (event) => {
                    event.stopPropagation()
                    setCollapsed((prev) => {
                      const next = new Set(prev)
                      if (next.has(folder)) next.delete(folder)
                      else next.add(folder)
                      return next
                    })
                  },
                }}
                onClick={() => {
                  setCollapsed((prev) => {
                    const next = new Set(prev)
                    if (next.has(folder)) next.delete(folder)
                    else next.add(folder)
                    return next
                  })
                }}
              />
              {expanded && list.map((flow) => (
                <SidebarTreeRow
                  key={flow.id}
                  padLeft={wsNavRowPadLeftPx(1)}
                  active={flow.id === activeDocumentId}
                  icon={getDocumentDisplayIcon({ kind: 'flow' }, { className: 'h-3.5 w-3.5 shrink-0' })}
                  label={flow.title}
                  badge={<span className="text-[11px] text-muted-foreground">{postureLabel(flow)}</span>}
                  onClick={(event) => openFlow(flow.id, 'definition', event)}
                  onDoubleClick={(event) => openFlow(flow.id, 'runs', event)}
                  onContextMenu={(event) => {
                    event.preventDefault()
                    setMenu({ x: event.clientX, y: event.clientY, flow })
                  }}
                />
              ))}
            </div>
          )
        })}
      </div>
      {menu && (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          entries={[
            { label: 'Open runs', onSelect: () => openFlow(menu.flow.id, 'runs') },
            { label: 'Replay', onSelect: () => { void triggerFlowRun(menu.flow.id, { trigger_type: 'manual' }) } },
            { label: 'Shadow', onSelect: () => { void promote(menu.flow, 'shadow') } },
            { label: 'Live', onSelect: () => { void promote(menu.flow, 'live') } },
            { label: 'Disable', onSelect: () => { void promote(menu.flow, 'disabled') } },
          ]}
        />
      )}
    </div>
  )
}
